import { useState } from 'react';

import { faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNotification } from '../Contexts/NotificationContext';

const DownloadItem = ({ item }) => {
    const { setNotification } = useNotification();
    const [isDownloading, setIsDownloading] = useState(false);
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleDownload = async () => {
        try {
            setIsDownloading(true);

            const response = await fetch(`${baseUrl}/painel/downloads/${item.id}/download`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao baixar o arquivo');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = item.nome.replace('.', '') || 'download';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="grid grid-cols-12 py-4 border-b border-gray-100 [&:last-child]:border-0 [&:last-child]:pb-0">
            <div className="col-span-7 flex items-center gap-2 sm:gap-4">
                <div className="relative w-10 sm:w-12 md:w-16 h-10 sm:h-12 md:h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                    {item.preview ? (
                        <img src={item.preview} alt={item.nome} className="w-full h-full object-cover" />
                    ) : (
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                            <span className="fa-layers fa-fw text-3xl sm:text-4xl md:text-5xl">
                                <FontAwesomeIcon icon={faFile} className="text-secondary" />
                                <span className="fa-layers-text fa-inverse text-[10px] sm:text-xs md:text-sm font-black mt-1 uppercase">
                                    {item.extensao}
                                </span>
                            </span>
                        </div>
                    )}
                </div>
                <div>
                    <h3 className="text-sm sm:text-xl xl:text-lg font-bold text-gray-900">{item.nome}</h3>
                </div>
            </div>

            <div className="col-span-3 sm:col-span-2 flex items-center justify-center text-xs sm:text-sm text-gray-500">
                {item.data_atualizacao}
            </div>

            <div className="col-span-2 sm:col-span-3 flex items-center justify-center">
                <button
                    onClick={handleDownload}
                    className="relative flex items-center gap-2 bg-tertiary text-white py-2 px-2 sm:px-4 rounded-full hover:bg-indigo-900 transition-all"
                >
                    <span className={`max-sm:hidden${isDownloading ? ' opacity-0' : ''}`}>Download</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-5 w-5${isDownloading ? ' opacity-0' : ''}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>

                    {isDownloading && (
                        <div role="status" className="absolute inset-0 flex justify-center items-center">
                            <svg
                                aria-hidden="true"
                                className="w-6 h-6 text-white text-opacity-20 animate-spin fill-secondary"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="currentColor"
                                />
                                <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="currentFill"
                                />
                            </svg>
                            <span className="sr-only">Loading...</span>
                        </div>
                    )}
                </button>
            </div>
        </div>
    );
};

export default DownloadItem;
